import React from 'react';
import SingleButtonDropdown from '@rio-cloud/rio-uikit/SingleButtonDropdown';

import { PageMetadata } from '@/generated/GeneratedApiTypes';

interface PaginationProps {
    pageMetadata?: PageMetadata;
    maxVisible?: number;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    onPageSizeChange?: (pageSize: number) => void;
}

const renderGoToFirstPage = (
    maxVisible: number,
    currentPage: number,
    totalPages: number,
    onPageChange: (page: number) => void,
) => {
    if (currentPage <= Math.ceil(maxVisible / 2) || totalPages <= maxVisible) {
        return null;
    }
    return (
        <li onClick={() => onPageChange(0)}>
            <a href="#">««</a>
        </li>
    );
};

const renderGoToLastPage = (
    maxVisible: number,
    currentPage: number,
    totalPages: number,
    onPageChange: (page: number) => void,
) => {
    if (totalPages - Math.floor(maxVisible / 2) <= currentPage || totalPages <= maxVisible) {
        return null;
    }
    return (
        <li onClick={() => onPageChange(totalPages - 1)}>
            <a href="#">»»</a>
        </li>
    );
};

const Pagination: React.FC<PaginationProps> = ({
    pageMetadata,
    maxVisible = 5,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [20, 40, 60],
}) => {
    if (!pageMetadata) return null;

    const { page_number: pageNumber = 0, total_pages: totalPages = 0, page_size: pageSize = 20 } = pageMetadata;

    if (totalPages <= 1) return null;

    // Calculate visible page numbers (up to maxVisible)
    const getVisiblePages = () => {
        const pages: number[] = [];

        let start = Math.max(0, pageNumber - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages - 1, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(0, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    };

    const visiblePages = getVisiblePages();

    const isFirstPage = pageNumber === 0;
    const isLastPage = pageNumber === totalPages - 1;

    return (
        <div className="pagination-component">
            {onPageSizeChange && (
                <SingleButtonDropdown
                    items={pageSizeOptions.map((size) => ({
                        label: size.toString(),
                        isSelected: pageSize === size,
                        value: size,
                        onSelect: () => {
                            onPageSizeChange(size);
                        },
                    }))}
                    title={pageSize.toString()}
                />
            )}
            <ul className="pagination">
                {renderGoToFirstPage(maxVisible, pageNumber, totalPages, onPageChange)}
                {!isFirstPage && (
                    <li onClick={() => onPageChange(pageNumber - 1)}>
                        <a href="#">«</a>
                    </li>
                )}
                {visiblePages.map((page) => (
                    <li key={page} onClick={() => onPageChange(page)} className={pageNumber === page ? 'active' : ''}>
                        <a href="#">{page + 1}</a>
                    </li>
                ))}

                {!isLastPage && (
                    <li onClick={() => onPageChange(pageNumber + 1)}>
                        <a href="#">»</a>
                    </li>
                )}
                {renderGoToLastPage(maxVisible, pageNumber, totalPages, onPageChange)}
            </ul>
        </div>
    );
};

export default Pagination;
